"use client"

import clsx from "clsx"
import { Loader2 } from "lucide-react"
import { memo, useCallback, useMemo, useState, type MouseEvent, type MouseEventHandler } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInteractiveProps } from "../../utils"
import { TEditorIcon } from "../display/TEditorIcon"
import { TIconName } from "../display/TIconPaths"

/**
 * @description 에디터 전용 아이콘 버튼. TIconButton과 유사하나 에디터 UI 스타일링에 최적화
 * @example
 * <TEditorIconButton name="pen" color="primary" size="small" onClick={handleDraw} />
 */
interface TEditorIconButtonProps extends BaseInteractiveProps {
	/** 버튼 요소 ref */
	ref?: React.Ref<HTMLButtonElement>
	/**
	 * 버튼 색상 변형
	 * @ai-style primary=강조색 배경, secondary=보조색 배경, tertiary=최소 강조,
	 *   line=테두리만, ghost=배경 없음(hover 시 표시), black=검정 배경, blurry=반투명 블러 배경
	 * @default "primary"
	 */
	color?: "primary" | "secondary" | "tertiary" | "line" | "ghost" | "black" | "blurry"
	/**
	 * 버튼 크기
	 * @ai-style xsmall=24px(아이콘16px), small=32px(아이콘20px), medium=40px(아이콘20px), large=48px(아이콘24px)
	 * @default "small"
	 */
	size?: "xsmall" | "small" | "medium" | "large"
	/**
	 * 선택(활성) 상태
	 * @ai-style true=눌린 상태 스타일 적용
	 */
	selected?: boolean
	/**
	 * 로딩 상태
	 * @ai-style true=스피너 표시, 클릭 비활성화
	 */
	loading?: boolean
	/** 아이콘 이름 (TIconName - v1 아이콘 세트) */
	name: TIconName
	/** 클릭 핸들러. Promise 반환 시 자동으로 로딩 상태 관리 */
	onClick?: (event: MouseEvent<HTMLButtonElement>) => Promise<void> | void
}

const editorIconSizeMap = {
	xsmall: 16,
	small: 20,
	medium: 20,
	large: 24
} as const

function TEditorIconButtonInner(props: TEditorIconButtonProps) {
	const {
		// HTML 속성
		id,
		className,
		// 스타일
		color = "primary",
		size = "small",
		// 상태
		selected,
		disabled,
		loading,
		// 아이콘
		name,
		// 데이터
		dataAttributes,
		// 핸들러
		onClick,
		// React 19 ref prop
		ref,
		"aria-label": ariaLabel,
		...restProps
	} = props

	const [processing, setProcessing] = useState<boolean>(false)
	const isLock = processing || loading
	const dataProps = useDataAttributes(dataAttributes)

	const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
		(event) => {
			if (disabled || loading || processing) {
				event.preventDefault()
				return
			}
			const result = onClick?.(event)
			if (result instanceof Promise) {
				setProcessing(true)
				result.finally(() => {
					setProcessing(false)
				})
			}
		},
		[disabled, loading, processing, onClick]
	)

	const iconSize = useMemo(() => editorIconSizeMap[size], [size])

	return (
		<button
			{...restProps}
			ref={ref}
			type="button"
			id={id}
			className={clsx("TIconButton", color, size, className)}
			disabled={disabled}
			aria-label={ariaLabel ?? `${name} button`}
			aria-pressed={selected}
			data-loading={isLock}
			data-selected={selected}
			{...dataProps}
			onClick={handleClick}
		>
			{isLock ? (
				<span className="loader">
					<Loader2 aria-hidden="true" style={{ width: iconSize, height: iconSize }} />
				</span>
			) : null}
			<span className={clsx("icon", { invisible: isLock })}>
				<TEditorIcon name={name} size={iconSize} />
			</span>
		</button>
	)
}

TEditorIconButtonInner.displayName = "TEditorIconButton"

export const TEditorIconButton = memo(TEditorIconButtonInner)
